import { useCallback, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { aiAssistantContext, AIAssistantDisplayMode } from '../../../../state/AIAssistantContextProvider';
import { aiAssistantEditorContext } from '../../../../state/AIAssistantEditorProvider';
import { getCurrentConversation } from '../../../../reducers/aiAssistant.selectors';
import { AsyncActionStatus } from '../../../../actions/common.actionTypes';

// Short delay to ensure the DOM has settled before scrolling.
// We use setTimeout over requestAnimationFrame because requestAnimationFrame
// pauses in background tabs, which would miss scroll updates during streaming.
export const SCROLL_DELAY_MS = 50;

/**
 * Central scroll behavior hook for the AI assistant chat.
 *
 * Current cases:
 * 1. User sends a message and the AI streams a response — chat stays at the bottom
 * 2. User opens the AI assistant dialog — chat scrolls to the latest message
 * 3. User opens a document artifact from meine inhalte and closes it — chat scrolls to that document's card
 * 4. User opens a document artifact from meine inhalte — the chat panel scrolls to the active document card
 * 5. User navigates to a conversation with an image scroll target — chat scrolls to that message
 */
const useAIAssistantAutoScroll = (): void => {
  const {
    dialogContentRef,
    isAssistantOpen,
    displayMode,
  } = useContext(aiAssistantContext);
  const {
    isTextEditorOpen,
    editorCloseScrollTarget,
    clearEditorCloseScrollTarget,
    activeDocumentCardRef,
    activeImageRef,
    imageScrollTarget,
    clearImageScrollTarget,
  } = useContext(aiAssistantEditorContext);
  const currentConversation = useSelector(getCurrentConversation);
  const isPageMode = displayMode === AIAssistantDisplayMode.PAGE;

  // This can be executed by two useEffects but it is safe
  // because scrollTo for the same position is idempotent.
  const scrollToBottom = useCallback((): ReturnType<typeof setTimeout> => {
    return setTimeout(() => {
      dialogContentRef?.current?.scrollTo({
        top: dialogContentRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }, SCROLL_DELAY_MS);
  }, [dialogContentRef]);

  // 1. User sends a message and the AI streams a response — chat stays at the bottom
  useEffect(() => {
    if (currentConversation?.status !== AsyncActionStatus.LOADING) return;
    const timerId = scrollToBottom();
    return () => clearTimeout(timerId);
  }, [currentConversation, scrollToBottom]);

  // 2. User opens the AI assistant dialog — chat scrolls to the latest message
  useEffect(() => {
    if (!isAssistantOpen) return;
    const timerId = scrollToBottom();
    return () => clearTimeout(timerId);
  }, [isAssistantOpen, scrollToBottom]);

  // 3, 4 & 5. User opens/closes a document artifact or navigates to an image
  useEffect(() => {
    if (!isPageMode) return;

    const hasDocumentTarget = !!editorCloseScrollTarget;
    const hasImageTarget = !!imageScrollTarget;
    if (!hasDocumentTarget && !hasImageTarget) return;

    const timerId = setTimeout(() => {
      if (hasDocumentTarget) {
        activeDocumentCardRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (!isTextEditorOpen) {
          clearEditorCloseScrollTarget();
        }
      } else if (hasImageTarget) {
        activeImageRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (activeImageRef?.current) {
          clearImageScrollTarget();
        }
      }
    }, SCROLL_DELAY_MS);

    return () => clearTimeout(timerId);
  }, [
    isTextEditorOpen,
    isPageMode,
    editorCloseScrollTarget,
    activeDocumentCardRef,
    clearEditorCloseScrollTarget,
    imageScrollTarget,
    activeImageRef,
    clearImageScrollTarget,
    currentConversation, // re-triggers when conversation loads so the ref-based scroll can retry
  ]);
};

export default useAIAssistantAutoScroll;
